import { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import api from "../axiosConfig";
import { dialogs } from "../context/DialogContext";
import { etiquetaKpiAgente } from "../utils/helpers";
import FichaResumenPropiedad from "../components/propiedades/FichaResumenPropiedad";
import { resolverPropiedadIdPorReferencia } from "../utils/resolverPropiedadIdPorReferencia";

const KPI_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#16a34a",
  "#0891b2",
  "#ca8a04",
  "#0d9488",
  "#4f46e5",
  "#c026d3",
];

function colorForAgent(nombre, index) {
  let h = 0;
  for (let i = 0; i < nombre.length; i += 1) h = (h * 31 + nombre.charCodeAt(i)) >>> 0;
  return KPI_COLORS[(h + index) % KPI_COLORS.length];
}

const EJEMPLO = `Juan\t\t658945907\tIdealista\tRef. 3211-C\tEspañol\t¿Cuántos dormitorios tiene?
Paco\t\t655127874\tFotocasa\tRef. 3209-C\tFrancés\t
Alberto\t636919000\tYaencontre\tRef. 10009-L\tCatalán\t¿Tiene ascensor?`;

const CASOS_TELEFONO = [
  { label: "Móvil ES simple", telefono: "612345678", email: "", whatsapp: "", pais: "España" },
  { label: "Móvil con +34", telefono: "+34 612 34 56 78", email: "", whatsapp: "", pais: "España" },
  { label: "Móvil con 0034", telefono: "0034 612345678", email: "", whatsapp: "", pais: "España" },
  { label: "Con espacios y paréntesis", telefono: "(+34) 612 34 56 78", email: "", whatsapp: "", pais: "España" },
  { label: "WhatsApp distinto campo", telefono: "", whatsapp: "612 34 56 78", email: "", pais: "España" },
  { label: "Con email también", telefono: "612345678", whatsapp: "", email: "prueba@vitalcasa.com", pais: "España" },
];

function BadgeTipo({ value, t }) {
  const styles = {
    exact: { bg: "rgba(34,197,94,0.15)", border: "rgba(34,197,94,0.35)", text: "#16a34a", label: "Exacta" },
    strong_probable: { bg: "rgba(245,158,11,0.15)", border: "rgba(245,158,11,0.35)", text: "#d97706", label: "Muy probable" },
    soft_probable: { bg: "rgba(59,130,246,0.15)", border: "rgba(59,130,246,0.35)", text: "#2563eb", label: "Probable" },
    invalid: { bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.28)", text: "#dc2626", label: "Inválido" },
  };
  const s = styles[value] || { bg: t.bgInput, border: t.border, text: t.textMuted, label: value || "—" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        background: s.bg,
        border: `1px solid ${s.border}`,
        color: s.text,
      }}
    >
      {s.label}
    </span>
  );
}

function ModalResultados({ abierto, filas, onCerrar, t, isDark }) {
  if (!abierto) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onCerrar}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(920px, 100%)",
          maxHeight: "85vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background: t.bgPanel,
          border: `1px solid ${t.border}`,
          borderRadius: 10,
          boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.5)" : "0 8px 24px rgba(0,0,0,0.15)",
        }}
      >
        <div
          style={{
            padding: "10px 14px",
            borderBottom: `1px solid ${t.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 14, color: t.textPrimary }}>
            Leads creados en pipeline ({filas.length})
          </span>
          <button
            type="button"
            onClick={onCerrar}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 18,
              color: t.textMuted,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ overflow: "auto", padding: "8px 12px 12px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ color: t.textMuted, textAlign: "left" }}>
                <th style={{ padding: "4px 6px" }}>#</th>
                <th style={{ padding: "4px 6px" }}>Lead</th>
                <th style={{ padding: "4px 6px" }}>Ref.</th>
                <th style={{ padding: "4px 6px" }}>Idioma</th>
                <th style={{ padding: "4px 6px" }}>Comercial</th>
                <th style={{ padding: "4px 6px" }}>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((r) => (
                <tr key={r.linea} style={{ borderTop: `1px solid ${t.border}` }}>
                  <td style={{ padding: "6px", color: t.textMuted }}>{r.linea}</td>
                  <td style={{ padding: "6px", color: t.textPrimary }}>
                    <div style={{ fontWeight: 600 }}>{r.nombre}</div>
                    <div style={{ fontSize: 11, color: t.textMuted }}>{r.telefono || "—"}</div>
                    {r.lead_id ? (
                      <div style={{ fontSize: 10, color: t.textMuted }}>ID: {r.lead_id.slice(0, 8)}…</div>
                    ) : null}
                    {r.ok === false ? (
                      <div style={{ fontSize: 10, color: "#dc2626" }}>{r.mensaje || r.error}</div>
                    ) : null}
                  </td>
                  <td style={{ padding: "6px", color: t.textPrimary }}>{r.referencia || "—"}</td>
                  <td style={{ padding: "6px", color: t.textPrimary }}>
                    <div>{r.idioma_label || r.idioma}</div>
                    {r.idioma_fuente === "message_heuristic" || r.idioma_fuente === "message_claude" ? (
                      <div style={{ fontSize: 10, color: t.textMuted }}>del mensaje</div>
                    ) : null}
                  </td>
                  <td style={{ padding: "6px", fontWeight: 600, color: r.agente_nombre ? t.accentBlue : "#dc2626" }}>
                    {r.agente_nombre || "Sin asignar"}
                  </td>
                  <td style={{ padding: "6px", color: t.textSecondary, maxWidth: 280 }}>{r.motivo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function formatFechaLead(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function DetallePreagentes({
  t,
  isDark,
  filas,
  cargando,
  error,
  agenteFiltroNombre,
  onLimpiarFiltro,
  onAbrirVistaPreviaRef,
}) {
  return (
    <div
      style={{
        marginTop: 12,
        border: `1px solid ${t.border}`,
        borderRadius: 8,
        padding: 10,
        background: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.02)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.04em" }}>
            DETALLE PREASIGNACIÓN
          </div>
          <div style={{ fontSize: 12, color: t.textPrimary, marginTop: 2 }}>
            {agenteFiltroNombre
              ? `Filtrado: ${agenteFiltroNombre}`
              : "Todos los leads activos del KPI (más recientes arriba)"}
            {cargando ? " · cargando…" : ` · ${filas.length} lead${filas.length !== 1 ? "s" : ""}`}
          </div>
        </div>
        {agenteFiltroNombre && (
          <button
            type="button"
            onClick={onLimpiarFiltro}
            style={{
              padding: "4px 8px",
              fontSize: 11,
              borderRadius: 6,
              border: `1px solid ${t.border}`,
              background: "transparent",
              color: t.accentBlue,
              cursor: "pointer",
            }}
          >
            Ver todos
          </button>
        )}
      </div>

      {cargando && filas.length === 0 ? (
        <div style={{ fontSize: 11, color: t.textMuted }}>Cargando detalle…</div>
      ) : error && filas.length === 0 ? (
        <div style={{ fontSize: 11, color: "#dc2626" }}>{error}</div>
      ) : filas.length === 0 ? (
        <div style={{ fontSize: 11, color: t.textMuted }}>Sin leads activos en este filtro.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 360, overflowY: "auto" }}>
          {filas.map((row) => (
            <div
              key={row.lead_id}
              style={{
                border: `1px solid ${t.border}`,
                borderRadius: 8,
                padding: "8px 10px",
                background: isDark ? "rgba(0,0,0,0.18)" : "#fff",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary }}>
                  {row.nombre}
                  {row.propiedad_ref ? (
                    <>
                      {" · "}
                      <button
                        type="button"
                        title="Abrir vista previa de la propiedad"
                        onClick={(e) => onAbrirVistaPreviaRef?.(e, row.propiedad_ref)}
                        style={{
                          padding: 0,
                          border: "none",
                          background: "none",
                          color: t.accentBlue,
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: "pointer",
                          textDecoration: "underline",
                          fontFamily: "inherit",
                        }}
                      >
                        Ref. {row.propiedad_ref}
                      </button>
                    </>
                  ) : (
                    <span style={{ fontWeight: 500, color: t.textMuted }}> · Sin ref.</span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: t.textMuted }}>{formatFechaLead(row.created_at)}</div>
              </div>
              <div style={{ fontSize: 11, color: t.textMuted, marginTop: 3 }}>
                Preagente: <strong style={{ color: t.textPrimary }}>{row.preagente_nombre || "—"}</strong>
                {row.titular_nombre && row.titular_nombre !== row.preagente_nombre
                  ? ` · Titular: ${row.titular_nombre}`
                  : ""}
                {row.fuente ? ` · ${row.fuente}` : ""}
              </div>
              <div style={{ fontSize: 11, color: t.textPrimary, marginTop: 4, lineHeight: 1.35 }}>
                Motivo: {row.motivo || "Sin motivo guardado"}
              </div>
              {row.formula_menor_carga && (
                <div
                  style={{
                    marginTop: 6,
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: isDark ? "rgba(37,99,235,0.12)" : "rgba(37,99,235,0.06)",
                    border: `1px solid ${isDark ? "rgba(37,99,235,0.35)" : "rgba(37,99,235,0.2)"}`,
                    fontSize: 11,
                    color: t.textPrimary,
                    lineHeight: 1.45,
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{row.formula_menor_carga.titulo}</div>
                  <div style={{ marginBottom: 4 }}>{row.formula_menor_carga.explicacion}</div>
                  {(row.formula_menor_carga.pasos || []).map((paso) => (
                    <div key={paso}>{paso}</div>
                  ))}
                  {row.formula_menor_carga.ejemplo_en_asignacion && (
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      En este lead: {row.formula_menor_carga.ejemplo_en_asignacion}
                    </div>
                  )}
                  {Array.isArray(row.formula_menor_carga.ranking_al_asignar) &&
                    row.formula_menor_carga.ranking_al_asignar.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>
                          % de carga justo antes de asignar
                        </div>
                        {row.formula_menor_carga.ranking_al_asignar.map((ag) => (
                          <div
                            key={`${ag.agente_id || ag.nombre}-${ag.orden}`}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 8,
                              fontWeight: ag.elegido ? 700 : 500,
                              color: ag.elegido ? t.textPrimary : t.textMuted,
                            }}
                          >
                            <span>
                              {ag.orden}. {ag.nombre}
                              {ag.elegido ? " ← elegido" : ""}
                            </span>
                            <span style={{ whiteSpace: "nowrap" }}>
                              {ag.leads != null && ag.cupo != null
                                ? `${ag.leads}/${ag.cupo}`
                                : "—"}
                              {ag.pct_lleno != null ? ` (${ag.pct_lleno} %)` : ""}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  {row.formula_menor_carga.carga_actual_pipeline != null &&
                    row.formula_menor_carga.max_leads_activos != null && (
                      <div style={{ marginTop: 4, color: t.textMuted }}>
                        Ahora mismo en el KPI: {row.formula_menor_carga.carga_actual_pipeline} de{" "}
                        {row.formula_menor_carga.max_leads_activos} del cupo
                        {row.formula_menor_carga.max_leads_activos > 0
                          ? ` (${Math.round(
                              (row.formula_menor_carga.carga_actual_pipeline /
                                row.formula_menor_carga.max_leads_activos) *
                                1000
                            ) / 10} % lleno)`
                          : ""}
                        .
                      </div>
                    )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RutinaAsignacionLeads({ t, isDark }) {
  const [texto, setTexto] = useState("");
  const [ejecutando, setEjecutando] = useState(false);
  const [ultimos, setUltimos] = useState([]);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [cargasReales, setCargasReales] = useState([]);
  const [cargandoKpis, setCargandoKpis] = useState(false);
  const [errorKpis, setErrorKpis] = useState(null);
  const [agenteFiltroId, setAgenteFiltroId] = useState(null);
  const [detalleFilas, setDetalleFilas] = useState([]);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState(null);
  const [vistaPreviaPropId, setVistaPreviaPropId] = useState(null);

  const abrirVistaPreviaRef = useCallback(async (e, ref) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const referencia = String(ref || "").trim();
    if (!referencia) return;
    const propId = await resolverPropiedadIdPorReferencia(referencia);
    if (propId) {
      setVistaPreviaPropId(propId);
      return;
    }
    await dialogs.alert(`No se encontró la propiedad ${referencia}`);
  }, []);

  const lineas = useMemo(
    () => texto.trim().split(/\r?\n/).filter((l) => l.trim()).length,
    [texto]
  );

  const agenteFiltroNombre = useMemo(() => {
    if (!agenteFiltroId) return null;
    const found = cargasReales.find((a) => String(a.id) === String(agenteFiltroId));
    return found?.nombre || null;
  }, [agenteFiltroId, cargasReales]);

  const cargarDetalle = useCallback(async (agenteId = null) => {
    setCargandoDetalle(true);
    setErrorDetalle(null);
    try {
      const qs = agenteId ? `?agente_id=${encodeURIComponent(agenteId)}` : "";
      const { data } = await api.get(`/pruebas/preagentes-detalle${qs}`, {
        timeout: 20000,
      });
      setDetalleFilas(data?.data || []);
    } catch (e) {
      setDetalleFilas([]);
      setErrorDetalle(
        e.code === "ECONNABORTED"
          ? "Tiempo agotado al cargar el detalle. Pulsa Actualizar KPIs."
          : e.response?.data?.error || e.message || "Error al cargar detalle"
      );
    } finally {
      setCargandoDetalle(false);
    }
  }, []);

  const cargarKpis = useCallback(async () => {
    setCargandoKpis(true);
    setErrorKpis(null);
    try {
      const { data } = await api.get("/pruebas/cargas-leads", { timeout: 20000 });
      setCargasReales(data?.data || []);
    } catch (e) {
      setErrorKpis(
        e.code === "ECONNABORTED"
          ? "Tiempo agotado al cargar KPIs. Pulsa Actualizar KPIs."
          : e.response?.data?.error || e.message || "Error al cargar KPIs"
      );
    } finally {
      setCargandoKpis(false);
    }
  }, []);

  useEffect(() => {
    cargarKpis();
  }, [cargarKpis]);

  useEffect(() => {
    cargarDetalle(agenteFiltroId);
  }, [agenteFiltroId, cargarDetalle]);

  const ejecutar = useCallback(async () => {
    if (!texto.trim()) {
      await dialogs.alert("Pega al menos una línea de leads.");
      return;
    }
    setEjecutando(true);
    try {
      const { data } = await api.post("/pruebas/asignacion-leads", { texto });
      const filas = data?.data || [];
      setUltimos(filas);
      setModalAbierto(true);
      if (data?.cargas) {
        setCargasReales(data.cargas);
      } else {
        await cargarKpis();
      }
      await cargarDetalle(agenteFiltroId);
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "Error al ejecutar");
    } finally {
      setEjecutando(false);
    }
  }, [texto, cargarKpis, cargarDetalle, agenteFiltroId]);

  return (
    <section
      style={{
        border: `1px solid ${t.border}`,
        borderRadius: 8,
        padding: 12,
        width: "100%",
        boxSizing: "border-box",
        background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: t.textPrimary }}>
            Asignación de leads (Ana)
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 11, color: t.textMuted }}>
            Crea leads reales asignados al comercial (excepción Pruebas: no pasan por Ana). Misma lógica de reglas que portales. Pega filas: nombre · teléfono · portal · ref. · idioma · mensaje
          </p>
        </div>
        {lineas > 0 && (
          <span style={{ fontSize: 11, color: t.textMuted }}>{lineas} línea{lineas !== 1 ? "s" : ""}</span>
        )}
      </div>

      <textarea
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder={EJEMPLO}
        rows={5}
        spellCheck={false}
        style={{
          width: "100%",
          marginTop: 8,
          padding: "8px 10px",
          fontSize: 11,
          fontFamily: "ui-monospace, monospace",
          lineHeight: 1.35,
          borderRadius: 6,
          border: `1px solid ${t.border}`,
          background: t.bgInput,
          color: t.textPrimary,
          resize: "vertical",
          minHeight: 72,
          boxSizing: "border-box",
        }}
      />

      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button
          type="button"
          disabled={ejecutando}
          onClick={ejecutar}
          style={{
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 6,
            border: "none",
            cursor: ejecutando ? "wait" : "pointer",
            background: t.accentBlue,
            color: "#fff",
            opacity: ejecutando ? 0.7 : 1,
          }}
        >
          {ejecutando ? "Creando leads…" : "Ejecutar"}
        </button>
        <button
          type="button"
          onClick={() => setTexto(EJEMPLO)}
          style={{
            padding: "6px 10px",
            fontSize: 11,
            borderRadius: 6,
            border: `1px solid ${t.border}`,
            background: "transparent",
            color: t.textMuted,
            cursor: "pointer",
          }}
        >
          Ejemplo
        </button>
        {ultimos.length > 0 && (
          <button
            type="button"
            onClick={() => setModalAbierto(true)}
            style={{
              padding: "6px 10px",
              fontSize: 11,
              borderRadius: 6,
              border: `1px solid ${t.border}`,
              background: "transparent",
              color: t.accentBlue,
              cursor: "pointer",
            }}
          >
            Ver último resultado
          </button>
        )}
        <button
          type="button"
          disabled={cargandoKpis || cargandoDetalle}
          onClick={async () => {
            await cargarKpis();
            await cargarDetalle(agenteFiltroId);
          }}
          style={{
            padding: "6px 10px",
            fontSize: 11,
            borderRadius: 6,
            border: `1px solid ${t.border}`,
            background: "transparent",
            color: t.textMuted,
            cursor: cargandoKpis || cargandoDetalle ? "wait" : "pointer",
          }}
        >
          {cargandoKpis || cargandoDetalle ? "Actualizando…" : "Actualizar KPIs"}
        </button>
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.04em", marginBottom: 8 }}>
          KPI carga comercial (BD) · clic en un círculo para filtrar
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 10,
            alignItems: "center",
            overflowX: "auto",
            paddingBottom: 4,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {cargandoKpis && cargasReales.length === 0 ? (
            <span style={{ fontSize: 11, color: t.textMuted }}>Cargando KPIs…</span>
          ) : errorKpis && cargasReales.length === 0 ? (
            <span style={{ fontSize: 11, color: "#dc2626" }}>{errorKpis}</span>
          ) : cargasReales.length === 0 ? (
            <span style={{ fontSize: 11, color: t.textMuted }}>Sin datos</span>
          ) : (
            cargasReales.map(({ id, nombre, total }, idx) => {
              const bg = colorForAgent(nombre, idx);
              const etiqueta = etiquetaKpiAgente(nombre);
              const seleccionado = String(agenteFiltroId || "") === String(id || "");
              return (
                <button
                  type="button"
                  key={id || nombre}
                  title={`${nombre}: ${total} leads · clic para ver detalle`}
                  onClick={() =>
                    setAgenteFiltroId((prev) =>
                      String(prev || "") === String(id || "") ? null : id
                    )
                  }
                  style={{
                    flex: "0 0 auto",
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: bg,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.35)" : "0 2px 6px rgba(0,0,0,0.12)",
                    border: seleccionado ? "2px solid #fff" : "2px solid transparent",
                    outline: seleccionado ? `2px solid ${bg}` : "none",
                    cursor: "pointer",
                    padding: 0,
                    fontFamily: "inherit",
                  }}
                >
                  <span
                    style={{
                      fontSize: etiqueta === "Ana" ? 8 : etiqueta.length >= 2 ? 10 : 12,
                      fontWeight: 800,
                      color: "rgba(255,255,255,0.92)",
                      lineHeight: 1,
                      letterSpacing: etiqueta.length >= 2 ? "-0.03em" : "0.02em",
                    }}
                  >
                    {etiqueta}
                  </span>
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 800,
                      color: "#fff",
                      lineHeight: 1.1,
                      marginTop: 2,
                    }}
                  >
                    {total}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      <DetallePreagentes
        t={t}
        isDark={isDark}
        filas={detalleFilas}
        cargando={cargandoDetalle}
        error={errorDetalle}
        agenteFiltroNombre={agenteFiltroNombre}
        onLimpiarFiltro={() => setAgenteFiltroId(null)}
        onAbrirVistaPreviaRef={abrirVistaPreviaRef}
      />

      <ModalResultados
        abierto={modalAbierto}
        filas={ultimos}
        onCerrar={() => setModalAbierto(false)}
        t={t}
        isDark={isDark}
      />

      {vistaPreviaPropId && (
        <FichaResumenPropiedad
          propiedadId={vistaPreviaPropId}
          onClose={() => setVistaPreviaPropId(null)}
        />
      )}
    </section>
  );
}

function RutinaTelefonos({ t, isDark }) {
  const [form, setForm] = useState({ telefono: "", whatsapp: "", email: "", pais: "España" });
  const [probando, setProbando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const probar = useCallback(async () => {
    if (!form.telefono.trim() && !form.whatsapp.trim() && !form.email.trim()) {
      await dialogs.alert("Escribe al menos un teléfono, WhatsApp o email.");
      return;
    }
    setProbando(true);
    try {
      const { data } = await api.post("/pruebas/telefonos", form);
      setResultado(data || null);
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "Error al probar teléfono");
    } finally {
      setProbando(false);
    }
  }, [form]);

  const setCaso = useCallback((caso) => {
    setForm({
      telefono: caso.telefono || "",
      whatsapp: caso.whatsapp || "",
      email: caso.email || "",
      pais: caso.pais || "España",
    });
  }, []);

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    fontSize: 12,
    borderRadius: 6,
    border: `1px solid ${t.border}`,
    background: t.bgInput,
    color: t.textPrimary,
    boxSizing: "border-box",
  };

  return (
    <section
      style={{
        marginTop: 14,
        border: `1px solid ${t.border}`,
        borderRadius: 8,
        padding: 12,
        width: "100%",
        boxSizing: "border-box",
        background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: t.textPrimary }}>
            Prueba de teléfonos
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 11, color: t.textMuted }}>
            Simula cómo entiende el CRM un teléfono y qué haría el sistema si el alta fuera manual o automática.
          </p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginTop: 10 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.04em" }}>TELÉFONO</span>
          <input value={form.telefono} onChange={(e) => setForm((p) => ({ ...p, telefono: e.target.value }))} style={inputStyle} placeholder="Ej. +34 612 34 56 78" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.04em" }}>WHATSAPP</span>
          <input value={form.whatsapp} onChange={(e) => setForm((p) => ({ ...p, whatsapp: e.target.value }))} style={inputStyle} placeholder="Opcional" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.04em" }}>EMAIL</span>
          <input value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} style={inputStyle} placeholder="Opcional" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.04em" }}>PAÍS BASE</span>
          <input value={form.pais} onChange={(e) => setForm((p) => ({ ...p, pais: e.target.value }))} style={inputStyle} placeholder="España" />
        </label>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
        <button
          type="button"
          disabled={probando}
          onClick={probar}
          style={{
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 6,
            border: "none",
            cursor: probando ? "wait" : "pointer",
            background: t.accentBlue,
            color: "#fff",
            opacity: probando ? 0.7 : 1,
          }}
        >
          {probando ? "Probando…" : "Probar teléfono"}
        </button>
        {CASOS_TELEFONO.map((caso) => (
          <button
            key={caso.label}
            type="button"
            onClick={() => setCaso(caso)}
            style={{
              padding: "6px 10px",
              fontSize: 11,
              borderRadius: 6,
              border: `1px solid ${t.border}`,
              background: "transparent",
              color: t.textMuted,
              cursor: "pointer",
            }}
          >
            {caso.label}
          </button>
        ))}
      </div>

      {resultado && (
        <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
          <div style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: 12, background: t.bgPanel }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.04em", marginBottom: 8 }}>
              CÓMO LO ENTIENDE EL SISTEMA
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
              {["telefono", "whatsapp"].map((key) => {
                const info = resultado.identities?.[key];
                if (!info) return null;
                return (
                  <div key={key} style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: t.textPrimary, marginBottom: 6 }}>
                      {key === "telefono" ? "Teléfono" : "WhatsApp"}
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted }}>Original: {info.raw || "—"}</div>
                    <div style={{ fontSize: 11, color: t.textMuted }}>E.164: {info.e164 || "—"}</div>
                    <div style={{ fontSize: 11, color: t.textMuted }}>Últimos 9: {info.last9 || "—"}</div>
                    <div style={{ fontSize: 11, color: t.textMuted }}>País: {info.country_iso || "—"}</div>
                    <div style={{ marginTop: 8 }}>
                      <BadgeTipo value={info.quality} t={t} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: 12, background: t.bgPanel }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.04em", marginBottom: 8 }}>
              QUÉ HARÍA EL SISTEMA
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
              <div style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 6 }}>Si el alta es manual</div>
                <div style={{ fontSize: 12, color: t.textSecondary, lineHeight: 1.5 }}>
                  {resultado.actions?.manual || "Sin acción definida"}
                </div>
              </div>
              <div style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 6 }}>Si entra como lead automático</div>
                <div style={{ fontSize: 12, color: t.textSecondary, lineHeight: 1.5 }}>
                  {resultado.actions?.automatico || "Sin acción definida"}
                </div>
              </div>
            </div>
          </div>

          <div style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: 12, background: t.bgPanel }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.04em" }}>
                COINCIDENCIAS DETECTADAS
              </div>
              <div style={{ fontSize: 11, color: t.textMuted }}>
                {resultado.total_matches || 0} coincidencia{resultado.total_matches === 1 ? "" : "s"}
              </div>
            </div>
            {!resultado.matches?.length ? (
              <div style={{ fontSize: 12, color: t.textMuted }}>No se han encontrado coincidencias con ese supuesto.</div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {resultado.matches.map((m) => (
                  <div key={`${m.entity_type}-${m.id}`} style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: 10 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary }}>{m.display_name}</div>
                      <BadgeTipo value={m.duplicate_type} t={t} />
                      <span style={{ fontSize: 11, color: t.textMuted }}>
                        {m.entity_type} · score {m.score}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
                      {m.telefono || m.whatsapp || "Sin teléfono"}
                      {m.email ? ` · ${m.email}` : ""}
                      {m.agente_nombre ? ` · agente ${m.agente_nombre}` : ""}
                    </div>
                    <div style={{ fontSize: 11, color: t.textSecondary, marginTop: 6 }}>
                      {m.reason || "Coincidencia detectada"}
                      {m.matched_on ? ` · por ${m.matched_on}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function RutinaRendimientoAgentes({ t, isDark }) {
  return (
    <section
      style={{
        marginBottom: 18,
        border: `1px solid ${t.border}`,
        borderRadius: 10,
        padding: 14,
        background: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.02)",
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.04em" }}>
        RENDIMIENTO AGENTES · ANA FRÍOS
      </div>
      <p style={{ margin: "6px 0 10px", fontSize: 12, color: t.textPrimary }}>
        Ranking Atención + Cualificación (score_asignacion) para el sorteo ponderado cuando Ana saca
        clientes de Fríos con cita. En sombra hasta activar ANA_FRIOS_ASSIGN_BY_SCORE.
      </p>
      <Link
        to="/agentes-rendimiento"
        style={{
          display: "inline-block",
          padding: "6px 12px",
          fontSize: 12,
          fontWeight: 700,
          borderRadius: 6,
          background: t.accentBlue || "#2563eb",
          color: "#fff",
          textDecoration: "none",
        }}
      >
        Abrir ranking
      </Link>
    </section>
  );
}

export default function Pruebas() {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";

  return (
    <div style={{ padding: "16px 20px", maxWidth: 960, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: t.textPrimary }}>
        Pruebas
      </h1>
      <p style={{ margin: "0 0 14px", fontSize: 12, color: t.textMuted }}>
        Rutinas de comprobación del CRM. Cada bloque usa el mismo código que producción.
      </p>
      <RutinaAsignacionLeads t={t} isDark={isDark} />
      <RutinaRendimientoAgentes t={t} isDark={isDark} />
      <RutinaTelefonos t={t} isDark={isDark} />
    </div>
  );
}
